import { createId } from "@paralleldrive/cuid2"
import {
  bigint,
  boolean,
  foreignKey,
  index,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core"
import { projectStatusEnum, projectsTable, stores } from "./app-schema"
import { user } from "./auth-schema"
import { configurationVersions } from "./store-configuration-schema"

// =============================================================================
// JSON value types (serializable through TanStack Start server functions)
// =============================================================================

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonValue[]
  | { [key: string]: JsonValue }
export type JsonObject = { [key: string]: JsonValue }

// =============================================================================
// Enums
// =============================================================================

export const changeProjectStatusEnum = pgEnum("change_project_status", [
  "draft",
  "queued",
  "previewing",
  "paused",
  "running",
  "ready_to_publish",
  "publishing",
  "published",
  "stopped",
  "closed",
  "failed",
])

export const changeProjectRunTypeEnum = pgEnum("change_project_run_type", [
  "preview",
  "apply",
  "publish",
  "export",
])

/**
 * Where one blueprint stands in the review, before publishing. A planner moves
 * it to `checked`, and only someone with approve rights signs it off from there
 * (BLU-679). There is no "declined": a blueprint that must not go out simply
 * never reaches `approved`, or leaves the selection entirely.
 */
export const changeProjectReviewStatusEnum = pgEnum("change_project_review_status", [
  "open",
  "checked",
  "approved",
])

export const changeProjectRunStatusEnum = pgEnum("change_project_run_status", [
  "pending",
  "running",
  "paused",
  "completed",
  "failed",
  "stopped",
  "retryable",
  "lease_expired",
])

export const changeProjectRunItemStatusEnum = pgEnum("change_project_run_item_status", [
  "pending",
  "running",
  "paused",
  "completed",
  "failed",
  "stopped",
  "retryable",
  "lease_expired",
])

// =============================================================================
// Tables
// =============================================================================

export const changeProjects = pgTable(
  "change_projects",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    name: text().notNull(),
    description: text(),
    ownerId: text("owner_id").notNull(),
    status: changeProjectStatusEnum().notNull().default("draft"),
    candidateQuery: jsonb("candidate_query").$type<JsonObject>().notNull().default({}),
    queryRevision: bigint("query_revision", { mode: "number" }).notNull().default(0),
    selectionRevision: bigint("selection_revision", { mode: "number" }).notNull().default(0),
    // The store configuration this bulk update works with; null => fall back to
    // the global active version.
    configVersionId: text("config_version_id"),
    /**
     * False when `configVersionId` points at a version the project merely uses
     * (BLU-679 follow-up): several country projects share one tuned version, so
     * deleting one of them must never take that version down with it.
     */
    ownsConfigVersion: boolean("owns_config_version").notNull().default(true),
    /** The project this one was started from, when it was copied from another. */
    copiedFromChangeProjectId: text("copied_from_change_project_id"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
    closedAt: timestamp("closed_at", { withTimezone: true }),
    deletedAt: timestamp("deleted_at", { withTimezone: true }),
    closedById: text("closed_by_id"),
  },
  (table) => [
    foreignKey({
      columns: [table.ownerId],
      foreignColumns: [user.id],
      name: "change_projects_owner_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.closedById],
      foreignColumns: [user.id],
      name: "change_projects_closed_by_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.configVersionId],
      foreignColumns: [configurationVersions.id],
      name: "change_projects_config_version_id_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.copiedFromChangeProjectId],
      foreignColumns: [table.id],
      name: "change_projects_copied_from_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    index("change_projects_owner_id_idx").on(table.ownerId),
    index("change_projects_status_idx").on(table.status),
    index("change_projects_created_at_idx").on(table.createdAt),
    index("change_projects_deleted_at_idx").on(table.deletedAt),
  ],
)

/**
 * The people a bulk update's review work is divided over. Deliberately not user
 * accounts: the client hands out the drawing work under names of their own
 * ("Drawer 1", "Drawer 2"), which do not map to logins, and several of them may
 * share one account. A person lives inside one bulk update and is copied along
 * when the next country's project is started from this one.
 */
export const changeProjectAssignees = pgTable(
  "change_project_assignees",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    changeProjectId: text("change_project_id").notNull(),
    name: text().notNull(),
    /**
     * Whether work may be signed off under this name. Approving is a right of
     * the person, not of the login: the client hands the drawing work out under
     * names of their own and only a few of those may approve, including their
     * own work. Handing this right out needs `project:approve`.
     */
    canApprove: boolean("can_approve").notNull().default(false),
    sortOrder: bigint("sort_order", { mode: "number" }).notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
    /** Taken out of the list, but kept so earlier checks still carry a name. */
    archivedAt: timestamp("archived_at", { withTimezone: true }),
  },
  (table) => [
    foreignKey({
      columns: [table.changeProjectId],
      foreignColumns: [changeProjects.id],
      name: "change_project_assignees_cp_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    uniqueIndex("change_project_assignees_cp_name_idx").on(table.changeProjectId, table.name),
    index("change_project_assignees_change_project_id_idx").on(table.changeProjectId),
  ],
)

export const changeProjectTargets = pgTable(
  "change_project_targets",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    changeProjectId: text("change_project_id").notNull(),
    /**
     * The store this target is about. A target follows the store, not a fixed
     * blueprint: `sourceProjectId` is the store's current blueprint and is
     * rebased when the store gets a newer one (BLU-677). Null for targets whose
     * source blueprint has no store link.
     */
    storeId: text("store_id"),
    sourceProjectId: text("source_project_id").notNull(),
    selected: boolean().notNull().default(true),
    reviewStatus: changeProjectReviewStatusEnum("review_status").notNull().default("open"),
    /**
     * Who moved this blueprint to `checked`, and when. The person comes first:
     * that is the name the client works with. The account is kept as the
     * fallback for projects that divide no work at all, and the audit log names
     * it either way.
     */
    checkedByAssigneeId: text("checked_by_assignee_id"),
    checkedById: text("checked_by_id"),
    checkedAt: timestamp("checked_at", { withTimezone: true }),
    /** Who signed it off, and when. Cleared when an approval is taken back. */
    approvedByAssigneeId: text("approved_by_assignee_id"),
    approvedById: text("approved_by_id"),
    approvedAt: timestamp("approved_at", { withTimezone: true }),
    /**
     * The person this store's review is handed to. Free-text `assignedTo` came
     * first and is left alone; it was never filled in.
     */
    assigneeId: text("assignee_id"),
    /** Legacy free-text reviewer label, superseded by `assigneeId`. */
    assignedTo: text("assigned_to"),
    /**
     * Planner's manual edit of this blueprint's end result (row assignments),
     * made in the in-flow editor.
     */
    customAssignments: jsonb("custom_assignments"),
    /**
     * The action chain's own result at the moment that edit was saved. The
     * difference between the two is the planner's work, per shelf row, so later
     * actions can be laid down first and their rows put back on top instead of
     * their work being thrown away. Null for edits made before this was kept,
     * which keep overriding the chain outright.
     */
    customAssignmentsBase: jsonb("custom_assignments_base"),
    queryRevision: bigint("query_revision", { mode: "number" }).notNull().default(0),
    selectionOrder: bigint("selection_order", { mode: "number" }).notNull().default(0),
    sourceProjectStatus: projectStatusEnum("source_project_status").notNull(),
    sourceProjectVersion: bigint("source_project_version", { mode: "number" }).notNull(),
    sourceProjectUpdatedAt: timestamp("source_project_updated_at", {
      withTimezone: true,
    }).notNull(),
    /**
     * Set when this target was moved to a newer blueprint of its store, and
     * cleared once the planner has reviewed the result again. Drives the notice
     * that explains why an earlier decision was dropped (BLU-677).
     */
    rebasedAt: timestamp("rebased_at", { withTimezone: true }),
    /**
     * What that move threw away, so the planner is only asked to look again when
     * there is something to look at: "review", "edit", "both", or null when the
     * blueprint simply changed under an untouched target.
     */
    rebaseDropped: text("rebase_dropped").$type<"review" | "edit" | "both">(),
    /**
     * False when the store currently has no blueprint with the status this
     * target was selected on (its blueprint was archived without a replacement).
     * Such a target sits out of the preview, the review and the publish, and
     * joins back in by itself once the store has one again.
     */
    sourceInScope: boolean("source_in_scope").notNull().default(true),
    /**
     * Set once this store's result went out: the blueprint that was created,
     * when, and in which publish run. A published target is done: it sits out
     * of the preview, the review, the target sync and every later publish, so
     * a bulk update can go out in parts without a store going twice (BLU-683).
     */
    publishedProjectId: text("published_project_id"),
    publishedAt: timestamp("published_at", { withTimezone: true }),
    publishedRunId: text("published_run_id"),
    /** Put in by hand after the selection was saved, not found by the query (BLU-678). */
    addedLater: boolean("added_later").notNull().default(false),
    addedLaterAt: timestamp("added_later_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
    removedAt: timestamp("removed_at", { withTimezone: true }),
  },
  (table) => [
    foreignKey({
      columns: [table.changeProjectId],
      foreignColumns: [changeProjects.id],
      name: "change_project_targets_cp_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.sourceProjectId],
      foreignColumns: [projectsTable.id],
      name: "change_project_targets_project_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.storeId],
      foreignColumns: [stores.id],
      name: "change_project_targets_store_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.assigneeId],
      foreignColumns: [changeProjectAssignees.id],
      name: "change_project_targets_assignee_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.checkedByAssigneeId],
      foreignColumns: [changeProjectAssignees.id],
      name: "change_project_targets_checked_by_assignee_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.approvedByAssigneeId],
      foreignColumns: [changeProjectAssignees.id],
      name: "change_project_targets_approved_by_assignee_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.checkedById],
      foreignColumns: [user.id],
      name: "change_project_targets_checked_by_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.approvedById],
      foreignColumns: [user.id],
      name: "change_project_targets_approved_by_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    uniqueIndex("change_project_targets_change_project_source_idx").on(
      table.changeProjectId,
      table.sourceProjectId,
    ),
    index("change_project_targets_change_project_id_idx").on(table.changeProjectId),
    index("change_project_targets_source_project_id_idx").on(table.sourceProjectId),
    index("change_project_targets_selected_idx").on(table.selected),
    index("change_project_targets_assignee_id_idx").on(table.assigneeId),
    index("change_project_targets_query_revision_idx").on(table.queryRevision),
  ],
)

/**
 * A signed-off blueprint, frozen (BLU-679 rounds). Approving a store snapshots
 * its end result: from then on that snapshot is the truth for the store, later
 * actions run over it, and publish emits exactly it. Every approval is its own
 * row and none is ever overwritten; withdrawing one marks it superseded and the
 * store computes from the one before it (or the source if there is none).
 *
 * Rounds are not stored: they are the distinct action counts these rows were
 * signed at, in order, plus the current chain when it has grown past them.
 */
export const changeProjectApprovals = pgTable(
  "change_project_approvals",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    changeProjectId: text("change_project_id").notNull(),
    targetId: text("target_id").notNull(),
    /** The end result as signed: row assignments, hand-tuned rows included. */
    snapshot: jsonb().notNull(),
    /** How long the action chain was; later actions are the ones past this. */
    actionCount: bigint("action_count", { mode: "number" }).notNull(),
    /** The source drawing underneath at the time, to notice when it moves on. */
    sourceFingerprint: text("source_fingerprint").notNull(),
    /** Which rows the planner had tuned by hand, for the record. */
    editedRowIds: jsonb("edited_row_ids").$type<string[]>().notNull().default([]),
    /**
     * Positions (0-based) of the actions that had changed this store by the time
     * it was signed. Ids are handed out afresh on every save, positions are not:
     * the frozen head of the chain keeps them stable. Lets a frozen action keep
     * saying how many stores it changed after those stores were signed.
     */
    affectedActionIndexes: jsonb("affected_action_indexes").$type<number[]>().notNull().default([]),
    /** What every action was called when this was signed, in chain order. */
    actionLabels: jsonb("action_labels").$type<string[]>().notNull().default([]),
    /** The check that preceded this signature, kept here because the audit trail is not forever. */
    checkedAt: timestamp("checked_at", { withTimezone: true }),
    checkedById: text("checked_by_id"),
    checkedByAssigneeId: text("checked_by_assignee_id"),
    approvedById: text("approved_by_id"),
    approvedByAssigneeId: text("approved_by_assignee_id"),
    approvedAt: timestamp("approved_at", { withTimezone: true }).defaultNow().notNull(),
    /** Set when withdrawn, or when the store moved to another drawing. */
    supersededAt: timestamp("superseded_at", { withTimezone: true }),
    supersededReason: text("superseded_reason").$type<"withdrawn" | "rebased" | "deselected">(),
  },
  (table) => [
    foreignKey({
      columns: [table.changeProjectId],
      foreignColumns: [changeProjects.id],
      name: "change_project_approvals_cp_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.targetId],
      foreignColumns: [changeProjectTargets.id],
      name: "change_project_approvals_target_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.approvedById],
      foreignColumns: [user.id],
      name: "change_project_approvals_user_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.approvedByAssigneeId],
      foreignColumns: [changeProjectAssignees.id],
      name: "change_project_approvals_assignee_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    index("change_project_approvals_target_id_idx").on(table.targetId),
    index("change_project_approvals_change_project_id_idx").on(table.changeProjectId),
  ],
)

export const changeProjectRequests = pgTable(
  "change_project_requests",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    changeProjectId: text("change_project_id").notNull(),
    sortOrder: bigint("sort_order", { mode: "number" }).notNull(),
    requestType: text("request_type").notNull(),
    request: jsonb().$type<JsonObject>().notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.changeProjectId],
      foreignColumns: [changeProjects.id],
      name: "change_project_requests_cp_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    uniqueIndex("change_project_requests_change_project_sort_idx").on(
      table.changeProjectId,
      table.sortOrder,
    ),
    index("change_project_requests_change_project_id_idx").on(table.changeProjectId),
    index("change_project_requests_request_type_idx").on(table.requestType),
  ],
)

export const changeProjectRuns = pgTable(
  "change_project_runs",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    changeProjectId: text("change_project_id").notNull(),
    runType: changeProjectRunTypeEnum("run_type").notNull(),
    status: changeProjectRunStatusEnum().notNull().default("pending"),
    requestedById: text("requested_by_id").notNull(),
    requestedAt: timestamp("requested_at", { withTimezone: true }).defaultNow().notNull(),
    startedAt: timestamp("started_at", { withTimezone: true }),
    pausedAt: timestamp("paused_at", { withTimezone: true }),
    resumedAt: timestamp("resumed_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    stoppedAt: timestamp("stopped_at", { withTimezone: true }),
    failedAt: timestamp("failed_at", { withTimezone: true }),
    totalCount: bigint("total_count", { mode: "number" }).notNull().default(0),
    completedCount: bigint("completed_count", { mode: "number" }).notNull().default(0),
    failedCount: bigint("failed_count", { mode: "number" }).notNull().default(0),
    retryableCount: bigint("retryable_count", { mode: "number" }).notNull().default(0),
    pausedCount: bigint("paused_count", { mode: "number" }).notNull().default(0),
    attemptCount: bigint("attempt_count", { mode: "number" }).notNull().default(0),
    lockOwner: text("lock_owner"),
    lockAcquiredAt: timestamp("lock_acquired_at", { withTimezone: true }),
    lockExpiresAt: timestamp("lock_expires_at", { withTimezone: true }),
    lastHeartbeatAt: timestamp("last_heartbeat_at", { withTimezone: true }),
    retryOfRunId: text("retry_of_run_id"),
    rootRunId: text("root_run_id"),
    metadata: jsonb().$type<JsonObject | null>().default(null),
    result: jsonb().$type<JsonObject | null>().default(null),
    error: jsonb().$type<JsonObject | null>().default(null),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.changeProjectId],
      foreignColumns: [changeProjects.id],
      name: "change_project_runs_cp_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.requestedById],
      foreignColumns: [user.id],
      name: "change_project_runs_user_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.retryOfRunId],
      foreignColumns: [table.id],
      name: "change_project_runs_retry_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.rootRunId],
      foreignColumns: [table.id],
      name: "change_project_runs_root_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    index("change_project_runs_change_project_id_idx").on(table.changeProjectId),
    index("change_project_runs_status_idx").on(table.status),
    index("change_project_runs_run_type_idx").on(table.runType),
    index("change_project_runs_requested_by_id_idx").on(table.requestedById),
    index("change_project_runs_lock_expires_at_idx").on(table.lockExpiresAt),
    index("change_project_runs_created_at_idx").on(table.createdAt),
  ],
)

export const changeProjectRunItems = pgTable(
  "change_project_run_items",
  {
    id: text().primaryKey().notNull().$defaultFn(createId),
    changeProjectRunId: text("change_project_run_id").notNull(),
    sourceProjectId: text("source_project_id").notNull(),
    status: changeProjectRunItemStatusEnum().notNull().default("pending"),
    selected: boolean().notNull().default(true),
    attemptCount: bigint("attempt_count", { mode: "number" }).notNull().default(0),
    maxAttempts: bigint("max_attempts", { mode: "number" }).notNull().default(3),
    orderIndex: bigint("order_index", { mode: "number" }).notNull().default(0),
    sourceProjectStatus: projectStatusEnum("source_project_status").notNull(),
    sourceProjectVersion: bigint("source_project_version", { mode: "number" }).notNull(),
    sourceProjectUpdatedAt: timestamp("source_project_updated_at", {
      withTimezone: true,
    }).notNull(),
    lockOwner: text("lock_owner"),
    lockAcquiredAt: timestamp("lock_acquired_at", { withTimezone: true }),
    lockExpiresAt: timestamp("lock_expires_at", { withTimezone: true }),
    lastHeartbeatAt: timestamp("last_heartbeat_at", { withTimezone: true }),
    startedAt: timestamp("started_at", { withTimezone: true }),
    pausedAt: timestamp("paused_at", { withTimezone: true }),
    resumedAt: timestamp("resumed_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    stoppedAt: timestamp("stopped_at", { withTimezone: true }),
    failedAt: timestamp("failed_at", { withTimezone: true }),
    retryOfItemId: text("retry_of_item_id"),
    rootItemId: text("root_item_id"),
    diagnostics: jsonb().$type<JsonObject | null>().default(null),
    result: jsonb().$type<JsonObject | null>().default(null),
    error: jsonb().$type<JsonObject | null>().default(null),
    publishedProjectId: text("published_project_id"),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.changeProjectRunId],
      foreignColumns: [changeProjectRuns.id],
      name: "change_project_run_items_run_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.sourceProjectId],
      foreignColumns: [projectsTable.id],
      name: "change_project_run_items_project_fk",
    })
      .onUpdate("cascade")
      .onDelete("cascade"),
    foreignKey({
      columns: [table.retryOfItemId],
      foreignColumns: [table.id],
      name: "change_project_run_items_retry_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.rootItemId],
      foreignColumns: [table.id],
      name: "change_project_run_items_root_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    foreignKey({
      columns: [table.publishedProjectId],
      foreignColumns: [projectsTable.id],
      name: "change_project_run_items_published_fk",
    })
      .onUpdate("cascade")
      .onDelete("set null"),
    uniqueIndex("change_project_run_items_run_source_idx").on(
      table.changeProjectRunId,
      table.sourceProjectId,
    ),
    index("change_project_run_items_change_project_run_id_idx").on(table.changeProjectRunId),
    index("change_project_run_items_source_project_id_idx").on(table.sourceProjectId),
    index("change_project_run_items_status_idx").on(table.status),
    index("change_project_run_items_lock_expires_at_idx").on(table.lockExpiresAt),
    index("change_project_run_items_published_project_id_idx").on(table.publishedProjectId),
    index("change_project_run_items_created_at_idx").on(table.createdAt),
  ],
)

// =============================================================================
// Types
// =============================================================================

export type ChangeProjectStatus = (typeof changeProjectStatusEnum.enumValues)[number]
export type ChangeProjectRunType = (typeof changeProjectRunTypeEnum.enumValues)[number]
export type ChangeProjectRunStatus = (typeof changeProjectRunStatusEnum.enumValues)[number]
export type ChangeProjectRunItemStatus = (typeof changeProjectRunItemStatusEnum.enumValues)[number]
export type ChangeProjectReviewStatus = (typeof changeProjectReviewStatusEnum.enumValues)[number]

export type ChangeProject = typeof changeProjects.$inferSelect
export type NewChangeProject = typeof changeProjects.$inferInsert

export type ChangeProjectAssignee = typeof changeProjectAssignees.$inferSelect
export type NewChangeProjectAssignee = typeof changeProjectAssignees.$inferInsert

export type ChangeProjectTarget = typeof changeProjectTargets.$inferSelect
export type NewChangeProjectTarget = typeof changeProjectTargets.$inferInsert

export type ChangeProjectApproval = typeof changeProjectApprovals.$inferSelect
export type NewChangeProjectApproval = typeof changeProjectApprovals.$inferInsert

export type ChangeProjectRequest = typeof changeProjectRequests.$inferSelect
export type NewChangeProjectRequest = typeof changeProjectRequests.$inferInsert

export type ChangeProjectRun = typeof changeProjectRuns.$inferSelect
export type NewChangeProjectRun = typeof changeProjectRuns.$inferInsert

export type ChangeProjectRunItem = typeof changeProjectRunItems.$inferSelect
export type NewChangeProjectRunItem = typeof changeProjectRunItems.$inferInsert
